"use client";

import { useEffect, useRef, useState } from "react";
import { locateJourney, type ChapterRange } from "@/lib/museum/progress";
import type { MuseumExperience } from "@/lib/museum/types";
import { JourneyMap } from "./JourneyMap";
import styles from "./museum.module.css";

const CALM_KEY = "museum-calm";

function engineAllowed(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (window.localStorage.getItem(CALM_KEY) === "1") return false;
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean };
    hardwareConcurrency?: number;
  };
  if (nav.connection?.saveData) return false;
  return (nav.hardwareConcurrency ?? 4) >= 4;
}

export function MuseumStage({
  movements,
  journey,
  children,
}: {
  movements: { id: string; title: string; threadLabel: string }[];
  journey: MuseumExperience["journey"];
  children: React.ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [live, setLive] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- capability detection must run after hydration
    setLive(engineAllowed());
  }, []);

  useEffect(() => {
    if (!rootRef.current) return;
    const root = rootRef.current;
    const chapters = Array.from(root.querySelectorAll<HTMLElement>("[data-museum-chapter]"));
    let ticking = false;

    function update() {
      ticking = false;
      const ranges: ChapterRange[] = chapters.map((chapter) => {
        const rect = chapter.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        return { top, bottom: top + rect.height };
      });
      const position = locateJourney(window.scrollY + window.innerHeight * 0.5, ranges);
      root.style.setProperty("--journey-progress", String(position.progress));
      chapters.forEach((chapter, index) => {
        chapter.dataset.active = index === position.activeIndex ? "true" : "false";
      });
      if (activeRef.current !== position.activeIndex) {
        activeRef.current = position.activeIndex;
        setActiveIndex(position.activeIndex);
      }
    }

    function scheduleUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    function focusHash() {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const target = chapters.find((chapter) => chapter.id === id);
      const heading = target?.querySelector<HTMLElement>("h1, h2");
      if (!target || !heading) return;
      target.scrollIntoView({ block: "start", behavior: "auto" });
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      scheduleUpdate();
    }

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    window.addEventListener("hashchange", focusHash);
    update();
    const hashTimer = window.setTimeout(focusHash, 180);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("hashchange", focusHash);
      window.clearTimeout(hashTimer);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-museum-root
      className={`${styles.museumRoot} ${live ? styles.live : styles.still}`}
    >
      <JourneyMap movements={movements} activeIndex={activeIndex} journey={journey} />
      {children}
      <button
        type="button"
        className={styles.calmButton}
        aria-pressed={!live}
        onClick={() => {
          const nextCalm = window.localStorage.getItem(CALM_KEY) !== "1";
          window.localStorage.setItem(CALM_KEY, nextCalm ? "1" : "0");
          setLive(engineAllowed());
        }}
      >
        {live ? "Calm viewing" : "Cinematic viewing"}
      </button>
    </div>
  );
}
